import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NcIcon from './NcIcon';
import FeedbackButton from './FeedbackButton';
import NotificationsModal from './NotificationsModal';
import NewsMenu from './NewsMenu';

const ROLE_TEXT = { provider: 'NordComply', admin: 'Admin', customer: 'Kunde' };

// Lives at the very bottom of the sidebar, replacing the old topbar's
// UserMenu. Styled as a compact "control center" grid of icon tiles per
// direct feedback (inspired by Windows' own quick-settings panel) rather
// than a list of text rows. `tenant`/`onOpenSyncStatus` are omitted on
// pages with no single tenant to report on (Org/Tenant portal), same as
// `onSync`/`syncing`.
export default function SidebarUserMenu({ tenant, onOpenSyncStatus, onSync, syncing }) {
  const { user, logout, role } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const displayName = user?.name || user?.email || '?';
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
    {/* In a customer's portal Nyheder stands next to Feedback; the Provider and Tenant portals have no news to read. */}
    {tenant !== undefined && <div className="sb-pair"><FeedbackButton /><NewsMenu /></div>}
    <div className="sidebar__user" ref={ref}>
      {open && (
        <div className="sidebar__user-popover">
          <div className="sidebar__cp-head">
            <span className="avatar">{initials}</span>
            <span className="sidebar__cp-who">
              <b>{displayName}</b>
              {user?.email && user.email !== displayName && <small title={user.email}>{user.email}</small>}
              {ROLE_TEXT[role] && <span className="sidebar__cp-role">{ROLE_TEXT[role]}</span>}
            </span>
          </div>

          <button type="button" className="sidebar__cp-row" onClick={toggleTheme}>
            <span className="sidebar__cp-ic"><NcIcon name={theme === 'dark' ? 'sun' : 'moon'} size={15} /></span>
            <span className="sidebar__cp-t">{theme === 'dark' ? 'Lyst tema' : 'Mørkt tema'}</span>
            <span className={`sidebar__cp-sw${theme === 'dark' ? ' is-on' : ''}`}><i /></span>
          </button>

          {onSync && (
            <button type="button" className={`sidebar__cp-row${syncing ? ' is-busy' : ''}`} onClick={onSync} disabled={syncing}>
              <span className="sidebar__cp-ic"><NcIcon name="refresh" size={15} /></span>
              <span className="sidebar__cp-t">{syncing ? 'Synkroniserer…' : 'Synkronisér nu'}</span>
            </button>
          )}

          {tenant !== undefined && <button type="button" className="sidebar__cp-row" onClick={() => { setNotificationsOpen(true); setOpen(false); }}>
            <span className="sidebar__cp-ic"><NcIcon name="bell" size={15} /></span>
            <span className="sidebar__cp-t">Notifikationer</span>
          </button>}

          {tenant !== undefined && (
            <button type="button" className="sidebar__cp-row" onClick={() => { onOpenSyncStatus?.(); setOpen(false); }}>
              <span className="sidebar__cp-ic"><NcIcon name="cloud" size={15} /></span>
              <span className="sidebar__cp-t">Forbindelse</span>
              <span className={`sidebar__cp-st ${tenant?.connectionOk === false ? 'is-bad' : 'is-ok'}`}><i />{tenant?.connectionOk === false ? 'Fejl' : 'Forbundet'}</span>
            </button>
          )}

          <div className="sidebar__cp-div" />
          <button type="button" className="sidebar__cp-row sidebar__cp-row--out" onClick={logout}>
            <span className="sidebar__cp-ic"><NcIcon name="logout" size={15} /></span>
            <span className="sidebar__cp-t">Log ud</span>
          </button>
        </div>
      )}
      <button type="button" className={`sidebar__user-btn${open ? ' is-open' : ''}`} onClick={() => setOpen((o) => !o)} title={user?.email}>
        <span className="avatar">{initials}</span>
        <span className="sidebar__collapsible-text sidebar__user-btn__name">{displayName}</span>
        <svg className="sidebar__user-btn__chev sidebar__collapsible-text" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
      </button>
    </div>
    {notificationsOpen && <NotificationsModal onClose={() => setNotificationsOpen(false)} currentTenant={tenant || null} />}
    </>
  );
}
